import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  TabContent,
  TabPane,
  Spinner,
  Alert,
  Badge,
} from "reactstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DashboardPageHeader, ResponsiveTabs } from "../../Components/Common/DashboardKit";

import HeaderFields from "./Form/HeaderFields";
import SetResponsibilitiesTab from "./Form/SetResponsibilitiesTab";
import RequirementGrid from "./Form/RequirementGrid";
import NotesTab from "./Form/NotesTab";
import AttachmentsTab from "./Form/AttachmentsTab";
import HistoricalTab from "./Form/HistoricalTab";
import WonLostBar from "./Form/WonLostBar";
import BottomActions from "./Form/BottomActions";
import useContractReviewMode from "./Form/useContractReviewMode";
import CustomerPickerModal from "./CustomerPickerModal";
import PrintPreviewModal from "./PrintPreviewModal";
import {
  useCrDetail,
  useCrSalesReps,
  useCrEmployees,
  useCrRequirements,
  useCrSave,
  useCrSubmit,
  useCrSaveAndSubmit,
  useCrCancel,
  useCrArchive,
  useCrWonLost,
  useCrDelete,
  useCrSetDefaults,
  useCrAddNote,
  useCrUploadAttachment,
  useCrDeleteAttachment,
  fetchEpicorPrefill,
} from "./Form/useContractReviewApi";
import useAuth from "../Personal Attendance/useAuth";

// ============================================================================
//   Detail page — composes header + 8 inner tabs.
// ============================================================================

export default function ContractReviewForm() {
  const { id: rawId } = useParams();
  const navigate = useNavigate();
  const isNew = !rawId || rawId === "new";
  const crId = isNew ? null : Number(rawId);

  const authUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("authUser") || "{}");
    } catch {
      return {};
    }
  }, []);
  const myEmpId = authUser?.uid;
  const [searchParams] = useSearchParams();

  // ----- queries -----
  const {
    data: cr,
    isLoading: loadingCr,
    error: crError,
    refetch,
  } = useCrDetail(crId);
  const { data: salesReps } = useCrSalesReps();
  const { data: employees } = useCrEmployees();
  // Go-live parity (2026-09-24): Contract Review admin = module "cr.admin" from the access
  // bundle (IT Admin always passes). It replaces the manager-list probe (archived-CR attach)
  // and the per-CR can-delete probe; the server enforces the same rules on every write.
  const auth = useAuth();
  const isCrAdmin = auth.isItAdmin || auth.canAccessModule("cr.admin");
  // Pre-fill from Epicor when arriving via /contract-review/new?soNum=X or ?quoteNum=Y.
  // Link source: ContractReviewTabs.js — Pending Creation rows. Mirrors legacy
  // ContractReviewFrm.InitializeNewCr which auto-pulls Customer/SalesRep on open.
  useEffect(() => {
    if (!isNew) return;
    const soNum = searchParams.get("soNum");
    const quoteNum = searchParams.get("quoteNum");
    if (!soNum && !quoteNum) return;
    (async () => {
      try {
        const pre = await fetchEpicorPrefill({
          soNum: soNum ? Number(soNum) : undefined,
          quoteNum: quoteNum ? Number(quoteNum) : undefined,
        });
        if (!pre || !pre.found) {
          toast.error(pre?.errorMessage || "Could not pre-fill from Epicor.");
          return;
        }
        setForm((f) => ({
          ...f,
          custName: pre.customerName || "",
          custId: pre.custId || "",
          salesRepCode: pre.salesPersonCode || "",
          salesOrderNum: pre.salesOrderNumber || null,
          quoteNumber: pre.quoteNumber || null,
        }));
      } catch (e) {
        toast.error(extractErr(e));
      }
    })();
    // eslint-disable-next-line
  }, [isNew, searchParams]);

  // ----- mode -----
  const mode = useContractReviewMode(cr, myEmpId, isCrAdmin, employees);

  // ----- local edit state -----
  // For a new CR we keep a blank form; for an existing one we hydrate from `cr`.
  const [form, setForm] = useState(() => emptyForm());
  const [keyRows, setKeyRows] = useState([]);
  const [salesRows, setSalesRows] = useState([]);
  const [engRows, setEngRows] = useState([]);
  const [matRows, setMatRows] = useState([]);
  const [qaRows, setQaRows] = useState([]);
  // Simple, robust dirty flag for assignment changes: flips true whenever the
  // author changes a row's employee (per-row or bulk), resets after a successful
  // save (and whenever a fresh CR loads). Drives "Save Assignments" visibility.
  const [assignmentsDirty, setAssignmentsDirty] = useState(false);

  // Set-defaults selections (for the "Set Responsibilities" tab)
  // Go-live parity (2026-09-24): S6 — legacy preselects neither Developmental nor Semi-Developmental
  // (ContractReviewFrm.vb:3792-3795); the user must choose before Set Defaults.
  const [developmentalSel, setDevelopmentalSel] = useState(null);
  const [engNA, setEngNA] = useState(false);
  const [matNA, setMatNA] = useState(false);
  const [qaNA, setQaNA] = useState(false);

  // -------------------------------------------------------------------------
  // Per-tab completion stats — drives tab badges (e.g. "Sales 3/4") and
  // the enhanced status banner. A row is "complete" when it has a non-blank
  // Response and a non-blank Record-Name trio if RecordNamePrompt is true.
  // Mirrors backend amIFinished + GetKeyFinished logic.
  //
  // MUST be declared AFTER the keyRows/salesRows/... useState hooks above so
  // it can read them — otherwise we hit a JS temporal-dead-zone ReferenceError.
  // -------------------------------------------------------------------------
  const completionStats = useMemo(() => {
    const tally = (rows) => {
      const total = (rows || []).length;
      let done = 0;
      for (const r of rows || []) {
        const respOk = !!(r.response && String(r.response).trim());
        const trioOk =
          !r.recordNamePrompt ||
          (!!(r.recordName && String(r.recordName).trim()) &&
            !!(r.storageLocation && String(r.storageLocation).trim()) &&
            !!(r.namingConvention && String(r.namingConvention).trim()));
        if (respOk && trioOk) done += 1;
      }
      return { done, total, remaining: total - done };
    };
    return {
      key: tally(keyRows),
      sales: tally(salesRows),
      eng: tally(engRows),
      mat: tally(matRows),
      qa: tally(qaRows),
    };
  }, [keyRows, salesRows, engRows, matRows, qaRows]);
  // Round 4 — legacy "Key Details first" gate (ContractReviewFrm.vb ~L885-894).
  // Before the author submits, ONLY the Set Responsibilities and Key Details
  // tabs are open; Sales / Engineering / Materials / Quality stay locked until
  // Key Details is fully answered. This reproduces the legacy two-phase flow
  // and removes the "I filled Sales during creation, then got emailed to do it
  // again" confusion — you can't fill a downstream section before its turn.
  const keyComplete =
    completionStats.key.total > 0 && completionStats.key.remaining === 0;
  // The gate is active only in the pre-submit author phase (new or saved draft
  // that hasn't been submitted yet). After submit, the normal cascade rules
  // (canEditResponses / responsibility) take over and nothing here applies.
  const preSubmitPhase = mode.isNew || (mode.isSaved && !cr?.authorSubmit);
  const keyGateActive = preSubmitPhase && !keyComplete;

  const { myRowsComplete, myRowsCount } = useMemo(() => {
    const me = Number(myEmpId);
    const trimmed = (s) => (s == null ? "" : String(s).trim());
    const allRows = [
      ...(keyRows || []),
      ...(salesRows || []),
      ...(engRows || []),
      ...(matRows || []),
      ...(qaRows || []),
    ];
    const mine = allRows.filter(r => Number(r.responsible) === me);
    const incomplete = mine.filter(r =>
      !trimmed(r.response) ||
      (r.recordNamePrompt && (!trimmed(r.recordName) || !trimmed(r.storageLocation) || !trimmed(r.namingConvention)))
    );
    return { myRowsComplete: incomplete.length === 0, myRowsCount: mine.length };
  }, [keyRows, salesRows, engRows, matRows, qaRows, myEmpId]);

  // Go-live parity (2026-09-24): N9 — legacy warns before closing a CR with unsaved responses or
  // notes (ContractReviewFrm.vb:3313-3338). Baseline = the state last loaded from the server.
  const [baseline, setBaseline] = useState(null);
  useEffect(() => {
    if (isNew) setBaseline(snapshotOf(form, keyRows, salesRows, engRows, matRows, qaRows));
    // eslint-disable-next-line
  }, []);
  const isDirty = useMemo(
    () => baseline != null && snapshotOf(form, keyRows, salesRows, engRows, matRows, qaRows) !== baseline,
    [baseline, form, keyRows, salesRows, engRows, matRows, qaRows]
  );
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  useEffect(() => {
    if (!cr) return;
    const loadedForm = {
      title: cr.title || "",
      custName: cr.custName || "",
      custId: cr.custId || "",
      salesRepCode: cr.salesRepCode || "",
      salesOrderNum: cr.salesOrderNum,
      quoteNumber: cr.quoteNumber,
      semiDevelopmental: cr.semiDevelopmental,
      developmental: cr.developmental,
      itarApply: cr.itarApply,
      statusRfq: cr.statusRfq,
      statusQuoted: cr.statusQuoted,
      statusPoReceived: cr.statusPoReceived,
      crCompletionDate: cr.crCompletionDate,
      stageSimilarToExistingPart: cr.stageSimilarToExistingPart,
      stageLeadTime: cr.stageLeadTime,
      stageNewCosting: cr.stageNewCosting,
      stageContractModification: cr.stageContractModification,
      stageLengthChange: cr.stageLengthChange,
      stageQaTerms: cr.stageQaTerms,
      stageInitialOpportunity: cr.stageInitialOpportunity,
      stageOther: cr.stageOther,
      descriptionOfPostQuoteChanges: cr.descriptionOfPostQuoteChanges,
    };
    setForm(loadedForm);
    setBaseline(snapshotOf(loadedForm, cr.keyResponses || [], cr.salesResponses || [],
      cr.engineeringResponses || [], cr.materialsResponses || [], cr.qualityResponses || []));
    // Key Details responsibility is auto-assigned to the CR author on the
    // BACKEND (Set Defaults seeds DEPT_KEY with head.CreatedAuthor), so we just
    // load whatever the server returns here — no client-side override (a client
    // override would wrongly use the *viewer's* id when a supervisor opens it).
    setKeyRows(cr.keyResponses || []);
    setSalesRows(cr.salesResponses || []);
    setEngRows(cr.engineeringResponses || []);
    setMatRows(cr.materialsResponses || []);
    setQaRows(cr.qualityResponses || []);
    // A freshly loaded/saved CR has no unsaved assignment changes yet.
    setAssignmentsDirty(false);
    // S6 — neither flag set yet (Set Defaults never run) = no choice made.
    setDevelopmentalSel(cr.developmental ? true : (cr.semiDevelopmental ? false : null));
    setEngNA(!!cr.engineeringNA);
    setMatNA(!!cr.materialsNA);
    setQaNA(!!cr.qualityNA);
  }, [cr]);

  // ----- inner tab state -----
  const [activeTab, setActiveTab] = useState("key");
  useEffect(() => {
    // If pre-submit, default to Set Responsibilities; else default to Key Details.
    if (mode.showSetResponsibilitiesTab) setActiveTab("setres");
    else setActiveTab("key");
    // eslint-disable-next-line
  }, [cr?.id]);

  // Round 4 — if the Key-first gate is active and the user is somehow parked on
  // a now-locked downstream tab, pull them back to Key Details.
  useEffect(() => {
    if (keyGateActive && ["sales", "eng", "mat", "qa"].includes(activeTab)) {
      setActiveTab("key");
    }
  }, [keyGateActive, activeTab]);

  // ----- mutations -----
  const saveMut = useCrSave();
  const submitMut = useCrSubmit();
  const saveSubmitMut = useCrSaveAndSubmit();
  const cancelMut = useCrCancel();
  const archiveMut = useCrArchive();
  const wonlostMut = useCrWonLost();
  const deleteMut = useCrDelete();
  const setDefaultsMut = useCrSetDefaults();
  const addNoteMut = useCrAddNote();
  const uploadAttMut = useCrUploadAttachment();
  const deleteAttMut = useCrDeleteAttachment();

  // ----- print modal -----
  const [printOpen, setPrintOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // ----- submit validation -----
  // Round 3 (UX): the set of header fields that failed the last Submit attempt.
  // Drives the persistent red alert at the top of the form and the per-field
  // red highlighting inside HeaderFields. Keyed by a stable field id; values
  // are the human-readable label shown in the alert.
  const [submitErrors, setSubmitErrors] = useState({});

  // ----- handlers -----
  const buildPayload = (finishLater) => ({
    ...form,
    crCompletionDate:
      form.crCompletionDate || new Date().toISOString().slice(0, 10),
    // ITAR may be null on a fresh form (the user hasn't picked Yes/No yet). The C# DTO
    // declares it as a non-nullable bool, so a literal null returns 400 at model binding.
    // Coerce null -> false for draft saves (Save & Finish Later, Set Defaults).
    // The Submit path is independently gated by validateForSubmit() which rejects null
    // before this runs, so Submit still requires a conscious Yes/No choice.
    itarApply: form.itarApply === true ? true : false,
    engineeringNA: cr?.authorSubmit ? cr.engineeringNA : engNA,
    materialsNA: cr?.authorSubmit ? cr.materialsNA : matNA,
    qualityNA: cr?.authorSubmit ? cr.qualityNA : qaNA,
    finishLater,
    resetGrids: false,
    keyResponses: keyRows,
    salesResponses: salesRows,
    engineeringResponses: engRows,
    materialsResponses: matRows,
    qualityResponses: qaRows,
    projectNotes: [], // notes are added via the Notes tab eagerly
    keepAttachmentIds: (cr?.attachments || []).map((a) => a.id),
  });

  const validateForSubmit = () => {
    // Round 3 (UX): collect EVERY missing field (don't stop at the first), so
    // the user sees the complete list in one pass. We also jump them to the
    // header tab and scroll to the top so the red alert + highlighted fields
    // are visible — previously a single toast flashed top-right while the user
    // was scrolled to the bottom on a different tab, so it was easy to miss.
    const errs = {};

    // Requirements must be loaded (Set Defaults was run at least once).
    const totalRows = (keyRows?.length || 0) + (salesRows?.length || 0) +
      (engRows?.length || 0) + (matRows?.length || 0) + (qaRows?.length || 0);
    if (totalRows === 0) {
      errs.requirements = "Open 'Set Responsibilities' and click 'Set Defaults' to load the requirement rows.";
    }

    // Round 4 — Key Details must be fully answered before the CR can be
    // submitted (legacy "awaiting completion of Key Details" gate).
    if (completionStats.key.total > 0 && completionStats.key.remaining > 0) {
      errs.keyIncomplete =
        `Key Details is not complete — ${completionStats.key.remaining} of ${completionStats.key.total} response(s) still need answers.`;
    }

    // Go-live parity (2026-09-24): C3 — legacy ValidateRows/validateGrid refuse the submit while
    // any Key or Sales row, or any row of an Engineering / Materials / Quality department that is
    // not N/A, has no employee (ContractReviewFrm.vb:2384-2559). The server checks the same rule.
    // N/A flags as they will be saved (same source as buildPayload).
    const naNow = {
      eng: cr?.authorSubmit ? !!cr.engineeringNA : engNA,
      mat: cr?.authorSubmit ? !!cr.materialsNA : matNA,
      qa: cr?.authorSubmit ? !!cr.qualityNA : qaNA,
    };
    const unassignedTab = [
      ["Key Details", keyRows, false],
      ["Sales", salesRows, false],
      ["Engineering", engRows, naNow.eng],
      ["Materials", matRows, naNow.mat],
      ["Quality", qaRows, naNow.qa],
    ].find(([, rows, na]) => !na && (rows || []).some((r) => !(Number(r.responsible) > 0)));
    if (unassignedTab) {
      errs.unassigned =
        `Please ensure that each entry in the '${unassignedTab[0]}' tab has a person selected in the 'Employee' column.`;
    }

    if (!form.title?.trim()) errs.title = "Title";
    if (!form.statusRfq && !form.statusQuoted && !form.statusPoReceived) {
      errs.phase = "Phase of Sale (RFQ / Quote / PO Received)";
    }
    if (!form.stageSimilarToExistingPart && !form.stageLeadTime && !form.stageNewCosting &&
      !form.stageContractModification && !form.stageLengthChange && !form.stageQaTerms &&
      !form.stageInitialOpportunity && !form.stageOther) {
      errs.stage = "Stage of This Project (pick at least one)";
    }
    if (!form.custId) errs.custId = "Customer (use the search button next to Customer ID)";
    if (!form.salesRepCode) errs.salesRep = "Salesperson";
    if (form.itarApply !== true && form.itarApply !== false) errs.itar = "ITAR Applies? (Yes or No)";
    // SO # / Quote # are optional — a CR can be submitted without either and they
    // can be added or updated later (the fields stay editable while the CR is
    // new or saved). Previously this required one of the two; that gate is removed.

    setSubmitErrors(errs);

    if (Object.keys(errs).length > 0) {
      // Bring the missing fields into view: header tab + scroll to top.
      setActiveTab("key");
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch { /* non-browser env */ }
      toast.error("Some required fields are missing — see the highlighted fields above.");
      return false;
    }

    return true;
  };

  // Go-live parity (2026-09-24): N4 — legacy requires an SO or Quote # unless the user confirms a
  // CR without one ("New CR Without SO / RFQ", ContractReviewNew.vb:498-516). Both stay optional
  // here, but a blank pair now asks first.
  const confirmNoSoQuote = () => {
    if (Number(form.salesOrderNum) > 0 || Number(form.quoteNumber) > 0) return true;
    return window.confirm(
      "This Contract Review has no Sales Order # or Quote #. This is for a CR that does not have a quote, RFQ, or sales order. Are you sure you wish to continue?",
    );
  };

  const handleSaveLater = async () => {
    // Go-live parity (2026-09-24): N3 — legacy requires a title even on Save & Finish Later
    // (ContractReviewFrm.vb:3169-3172).
    if (!form.title?.trim()) {
      setSubmitErrors({ title: "Title" });
      toast.error("Please enter a title before continuing.");
      return;
    }
    if (isNew && !confirmNoSoQuote()) return;
    try {
      const payload = buildPayload(true);
      const { id: newId } = await saveMut.mutateAsync({ id: crId, payload });
      toast.success("Saved.");
      if (isNew && newId) navigate(`/contract-review/${newId}`);
      else refetch();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleSaveClose = async () => {
    try {
      const payload = buildPayload(false);
      await saveMut.mutateAsync({ id: crId, payload });
      toast.success("Saved.");
      navigate("/contract-review");
    } catch (e) {
      toast.error(extractErr(e));
    }
  };
  const handleSaveDraft = async () => {
    try {
      const payload = buildPayload(false);
      await saveMut.mutateAsync({ id: crId, payload });
      toast.success("Draft saved.");
      refetch();
    } catch (e) { toast.error(extractErr(e)); }
  };
  // Re-assignment save (In-Progress / Ready). Persists the CR (which includes the
  // changed responsible assignments); the backend emails anyone newly assigned.
  const handleSaveAssignments = async () => {
    try {
      const payload = buildPayload(false);
      await saveMut.mutateAsync({ id: crId, payload });
      setAssignmentsDirty(false);
      toast.success("Assignments saved. Newly assigned people have been emailed.");
      refetch();
    } catch (e) { toast.error(extractErr(e)); }
  };
  const handleSubmit = async () => {
    console.log("[CR Submit] clicked. crId =", crId, "myEmpId =", myEmpId);
    if (!validateForSubmit()) {
      console.log("[CR Submit] blocked by validation");
      return;
    }
    setSubmitErrors({}); // validation passed — clear any prior highlights
    // Go-live parity (2026-09-24): N3 — legacy confirms a Complete-by date of today
    // (ContractReviewFrm.vb:3722-3727).
    if (isToday(form.crCompletionDate) && !window.confirm(
      "Warning: you have indicated that the Contract Review should be completed today. Typically a CR should be given ample amount of time to be properly reviewed. Are you sure you wish to indicate that the CR must be completed by today?",
    )) return;
    if (!confirmNoSoQuote()) return;
    try {
      const payload = buildPayload(false);
      // Go-live parity (2026-09-24): S11 — save + submit in ONE server transaction (was a PUT then
      // a POST /submit; a failed second call left the CR invisible in every list).
      await saveSubmitMut.mutateAsync({ id: crId, payload });
      console.log("[CR Submit] save-and-submit OK");
      // Round 25: the native alert() here duplicated the toast (two popups
      // per submit). The toast is the single notification now.
      toast.success("Contract Review has been successfully submitted.");
      navigate("/contract-review");
    } catch (e) {
      console.error("[CR Submit] ERROR", e, e?.response?.data);
      const msg = extractErr(e);
      toast.error(msg);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        "This CR is currently collecting responses from various individuals. Are you sure you wish to cancel this CR? " +
        "This action will move the CR back to 'Saved' and will become inactive for now.",
      )
    )
      return;
    try {
      await cancelMut.mutateAsync(crId);
      toast.success("CR has been canceled and moved to Saved.");
      navigate("/contract-review");
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleArchive = async () => {
    try {
      await archiveMut.mutateAsync(crId);
      toast.success("CR archived.");
      navigate("/contract-review");
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleWonLostSave = async (won, lost) => {
    try {
      await wonlostMut.mutateAsync({
        id: crId,
        payload: { won, lost, archive: true },
      });
      toast.success("Disposition saved and CR archived.");
      navigate("/contract-review");
    } catch (e) {
      toast.error(extractErr(e));
    }
  };
  const [wonSel, setWonSel] = useState(false);
  const [lostSel, setLostSel] = useState(false);
  useEffect(() => {
    setWonSel(cr?.won === true);
    setLostSel(cr?.lost === true);
  }, [cr?.won, cr?.lost]);

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you wish to completely delete this Contract Review?",
      )
    )
      return;
    try {
      await deleteMut.mutateAsync(crId);
      toast.success("CR deleted.");
      navigate("/contract-review");
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleSetDefaults = async (payload) => {
    try {
      // For a brand-new CR we need to save the header first so it gets an id,
      // THEN apply the defaults in the same click. Without this, the first
      // click would only save the CR and the user would have to click Set
      // Defaults a second time to actually load the requirement rows —
      // which is what users experienced as "tables not coming the first time".
      let targetId = crId;
      if (!targetId) {
        const init = buildPayload(true);
        const saved = await saveMut.mutateAsync({ id: null, payload: init });
        targetId = saved?.id;
        if (!targetId) {
          toast.error("Save did not return an ID — cannot apply defaults.");
          return;
        }
      }
      await setDefaultsMut.mutateAsync({ id: targetId, payload });
      toast.success("Defaults applied.");
      if (!crId) {
        // First-time creation path: move into the saved CR's URL so
        // subsequent saves use PUT/{id} and the form re-hydrates with
        // the freshly-seeded requirement rows.
        navigate(`/contract-review/${targetId}`);
      } else {
        refetch();
      }
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleAddNote = async (text) => {
    if (!crId) {
      toast.error("Save the CR first before adding notes.");
      return;
    }
    try {
      await addNoteMut.mutateAsync({ id: crId, note: text });
      refetch();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleUpload = async (file) => {
    if (!crId) {
      toast.error("Save the CR first before uploading attachments.");
      return;
    }
    await uploadAttMut.mutateAsync({ id: crId, file });
    refetch();
  };
  const handleDeleteAttachment = async (attId) => {
    try {
      await deleteAttMut.mutateAsync({ attId });
      refetch();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleCustomerPick = ({ custId, name }) => {
    setForm((f) => ({ ...f, custId, custName: name }));
    setPickerOpen(false);
  };

  // ----- render -----
  if (loadingCr && !isNew) {
    return (
      <div className="page-content">
        <Container fluid>
          <Spinner /> Loading…
        </Container>
      </div>
    );
  }
  if (crError && !isNew) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">Could not load CR.</Alert>
        </Container>
      </div>
    );
  }

  const statusBanner = computeStatusBanner(cr, mode, myEmpId, completionStats);

  return (
    <div className="page-content">
      <Container fluid>
        <DashboardPageHeader
          icon="ri-file-text-line"
          eyebrow="Contract Review"
          title={
            isNew ? "New Contract Review" : `CR #${crId} — ${cr?.title || ""}`
          }
          subtitle={
            isNew
              ? "Author a new contract review and route for approval."
              : "Review, edit, and progress this contract through its workflow."
          }
          tone="teal"
          breadcrumb={[
            { label: "Dashboard", to: "/" },
            { label: "Contract Review", to: "/contract-review" },
            { label: isNew ? "New" : `CR #${crId}` },
          ]}
        />

        {statusBanner && (
          <Alert color={statusBanner.color}>{statusBanner.text}</Alert>
        )}

        {Object.keys(submitErrors).length > 0 && (
          <Alert color="danger" className="border-danger">
            <div className="d-flex align-items-start gap-2">
              <i className="ri-error-warning-line fs-5 mt-1" />
              <div>
                <div className="fw-semibold mb-1">
                  This Contract Review can&rsquo;t be submitted yet — please complete the highlighted field
                  {Object.keys(submitErrors).length > 1 ? "s" : ""}:
                </div>
                <ul className="mb-0 ps-3">
                  {submitErrors.requirements && <li>{submitErrors.requirements}</li>}
                  {submitErrors.keyIncomplete && <li>{submitErrors.keyIncomplete}</li>}
                  {submitErrors.unassigned && <li>{submitErrors.unassigned}</li>}
                  {submitErrors.title && <li>Title</li>}
                  {submitErrors.custId && <li>{submitErrors.custId}</li>}
                  {submitErrors.salesRep && <li>Salesperson</li>}
                  {submitErrors.phase && <li>{submitErrors.phase}</li>}
                  {submitErrors.itar && <li>{submitErrors.itar}</li>}
                  {submitErrors.stage && <li>{submitErrors.stage}</li>}
                </ul>
              </div>
            </div>
          </Alert>
        )}

        {mode.showWonLostBar && (
          <WonLostBar
            won={wonSel}
            lost={lostSel}
            onChange={(w, l) => {
              setWonSel(w);
              setLostSel(l);
            }}
            onSubmit={() => handleWonLostSave(wonSel, lostSel)}
            disabled={!mode.canWonLost || wonlostMut.isPending}
          />
        )}

        <Card>
          <CardHeader>
            <h5 className="mb-0">
              {isNew ? "New Contract Review" : `Contract Review #${cr?.id}`}
              {cr?.archived && (
                <Badge color="dark" className="ms-2">
                  Archived
                </Badge>
              )}
              {cr?.isReady && !cr?.archived && (
                <Badge color="success" className="ms-2">
                  Ready
                </Badge>
              )}
              {cr?.saved && (
                <Badge color="warning" className="ms-2">
                  Saved (Pending Submit)
                </Badge>
              )}
              {!cr?.saved &&
                cr?.authorSubmit &&
                !cr?.isReady &&
                !cr?.archived && (
                  <Badge color="info" className="ms-2">
                    In Progress
                  </Badge>
                )}
              {cr?.leadTimeDays != null && (
                <Badge color="primary" className="ms-2">
                  {Number(cr.leadTimeDays).toFixed(1)} days of lead time needed
                </Badge>
              )}
            </h5>
          </CardHeader>
          <CardBody>
            <HeaderFields
              form={form}
              setForm={setForm}
              salesReps={salesReps}
              onOpenCustomerPicker={() => setPickerOpen(true)}
              readOnly={!mode.canEditHeader}
              canEditEpicorFields={
                mode.canEditHeader && (mode.isNew || mode.isSaved)
              }
              errors={submitErrors}
            />

            <ResponsiveTabs
              tabs={[
                ...(mode.showSetResponsibilitiesTab
                  ? [{ key: "setres", label: "Set Responsibilities" }]
                  : []),
                { key: "key",   label: "Key Details", badge: renderCompletionBadge(completionStats.key) },
                { key: "sales", label: "Sales",       badge: renderCompletionBadge(completionStats.sales),
                  disabled: keyGateActive, title: keyGateActive ? "Complete Key Details first" : undefined },
                ...(!cr?.engineeringNA
                  ? [{ key: "eng", label: "Engineering", badge: renderCompletionBadge(completionStats.eng),
                       disabled: keyGateActive, title: keyGateActive ? "Complete Key Details first" : undefined }]
                  : []),
                ...(!cr?.materialsNA
                  ? [{ key: "mat", label: "Materials", badge: renderCompletionBadge(completionStats.mat),
                       disabled: keyGateActive, title: keyGateActive ? "Complete Key Details first" : undefined }]
                  : []),
                ...(!cr?.qualityNA
                  ? [{ key: "qa", label: "Quality", badge: renderCompletionBadge(completionStats.qa),
                       disabled: keyGateActive, title: keyGateActive ? "Complete Key Details first" : undefined }]
                  : []),
                { key: "hist",  label: "Historical" },
                { key: "notes", label: "Notes" },
                { key: "att",   label: "Attachments" },
              ]}
              activeKey={activeTab}
              onChange={setActiveTab}
              variant="underline"
              color="primary"
              className="mb-3 mt-3"
            />

            <TabContent activeTab={activeTab}>
              {mode.showSetResponsibilitiesTab && (
                <TabPane tabId="setres">
                  <SetResponsibilitiesTab
                    developmental={developmentalSel}
                    setDevelopmental={setDevelopmentalSel}
                    engineeringNA={engNA}
                    setEngineeringNA={setEngNA}
                    materialsNA={matNA}
                    setMaterialsNA={setMatNA}
                    qualityNA={qaNA}
                    setQualityNA={setQaNA}
                    employees={employees}
                    onSetDefaults={handleSetDefaults}
                    defaultsApplied={keyRows.length + salesRows.length > 0}
                    isSettingDefaults={setDefaultsMut.isPending}
                    keyRows={keyRows}
                    salesRows={salesRows}
                    engRows={engRows}
                    matRows={matRows}
                    qaRows={qaRows}
                  />
                </TabPane>
              )}
              <TabPane tabId="key">
                {keyGateActive && (
                  <Alert color="info" className="d-flex align-items-start gap-2">
                    <i className="ri-lock-line fs-5 mt-1" />
                    <div>
                      <div className="fw-semibold">Complete Key Details first</div>
                      <div className="small">
                        Fill in every Key Details response below. The Sales, Engineering,
                        Materials and Quality tabs unlock once Key Details is complete — this
                        mirrors the original Contract Review flow, where downstream departments
                        are only notified after the key information is captured.
                        {completionStats.key.total > 0 && (
                          <> &nbsp;<strong>{completionStats.key.remaining}</strong> of{" "}
                          <strong>{completionStats.key.total}</strong> response
                          {completionStats.key.remaining === 1 ? "" : "s"} left.</>
                        )}
                      </div>
                    </div>
                  </Alert>
                )}
                <RequirementGrid
                  title="Key Details of Sale"
                  rows={keyRows}
                  onChangeRow={(i, p) => { if ("responsible" in p) setAssignmentsDirty(true); setKeyRows((rs) => patch(rs, i, p)); }}
                  onBulkAssign={(empId) => { setAssignmentsDirty(true); setKeyRows((rs) => rs.map((r) => ({ ...r, responsible: empId }))); }}
                  employees={employees}
                  readOnly={!mode.canEditResponses}
                  myEmpId={myEmpId}
                  isAuthor={mode.isAuthor}
                  authorSubmitted={!!cr?.authorSubmit}
                  hideBulkAssign
                  lockEmployeeToName
                />
              </TabPane>
              <TabPane tabId="sales">
                <RequirementGrid
                  title="Sales Requirements"
                  rows={salesRows}
                  onChangeRow={(i, p) => { if ("responsible" in p) setAssignmentsDirty(true); setSalesRows((rs) => patch(rs, i, p)); }}
                  onBulkAssign={(empId) => { setAssignmentsDirty(true); setSalesRows((rs) => rs.map((r) => ({ ...r, responsible: empId }))); }}
                  employees={employees}
                  readOnly={!mode.canEditResponses}
                  myEmpId={myEmpId}
                  isAuthor={mode.isAuthor}
                  authorSubmitted={!!cr?.authorSubmit}
                />
              </TabPane>
              {!cr?.engineeringNA && (
                <TabPane tabId="eng">
                  <RequirementGrid
                    title="Engineering Requirements"
                    rows={engRows}
                    onChangeRow={(i, p) => { if ("responsible" in p) setAssignmentsDirty(true); setEngRows((rs) => patch(rs, i, p)); }}
                    onBulkAssign={(empId) => { setAssignmentsDirty(true); setEngRows((rs) => rs.map((r) => ({ ...r, responsible: empId }))); }}
                    employees={employees}
                    readOnly={!mode.canEditResponses}
                    myEmpId={myEmpId}
                    isAuthor={mode.isAuthor}
                    authorSubmitted={!!cr?.authorSubmit}
                  />
                </TabPane>
              )}
              {!cr?.materialsNA && (
                <TabPane tabId="mat">
                  <RequirementGrid
                    title="Materials Requirements"
                    rows={matRows}
                    onChangeRow={(i, p) => { if ("responsible" in p) setAssignmentsDirty(true); setMatRows((rs) => patch(rs, i, p)); }}
                    onBulkAssign={(empId) => { setAssignmentsDirty(true); setMatRows((rs) => rs.map((r) => ({ ...r, responsible: empId }))); }}
                    employees={employees}
                    readOnly={!mode.canEditResponses}
                    myEmpId={myEmpId}
                    isAuthor={mode.isAuthor}
                    authorSubmitted={!!cr?.authorSubmit}
                  />
                </TabPane>
              )}
              {!cr?.qualityNA && (
                <TabPane tabId="qa">
                  <RequirementGrid
                    title="Quality Requirements"
                    rows={qaRows}
                    onChangeRow={(i, p) => { if ("responsible" in p) setAssignmentsDirty(true); setQaRows((rs) => patch(rs, i, p)); }}
                    onBulkAssign={(empId) => { setAssignmentsDirty(true); setQaRows((rs) => rs.map((r) => ({ ...r, responsible: empId }))); }}
                    employees={employees}
                    readOnly={!mode.canEditResponses}
                    myEmpId={myEmpId}
                    isAuthor={mode.isAuthor}
                    authorSubmitted={!!cr?.authorSubmit}
                  />
                </TabPane>
              )}
              <TabPane tabId="hist">
                <HistoricalTab
                  custId={form.custId}
                  currentCrId={crId || 0}
                  sswPart={extractSswPart(keyRows)}
                />
              </TabPane>
              <TabPane tabId="notes">
                {/* Go-live parity (2026-09-24): C5 — Add Note only for participants / cr.admin, never on an archived CR. */}
                <NotesTab
                  notes={cr?.projectNotes}
                  onAddNote={handleAddNote}
                  isAdding={addNoteMut.isPending}
                  readOnly={!mode.canAddNotes}
                  isArchived={mode.isArchived}
                />
              </TabPane>
              <TabPane tabId="att">
                {/* Go-live parity (2026-09-24): C5 — Upload only for participants / cr.admin; archived CR: cr.admin only. */}
                <AttachmentsTab
                  attachments={cr?.attachments}
                  onUpload={handleUpload}
                  onDelete={handleDeleteAttachment}
                  isUploading={uploadAttMut.isPending}
                  readOnly={!mode.canUploadAttachments}
                  canAttachAfterArchive={mode.canAttachAfterArchive}
                  isArchived={mode.isArchived}
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>

        <BottomActions
          mode={mode}
          onSubmit={handleSubmit}
          onSaveLater={handleSaveLater}
          onSaveDraft={handleSaveDraft}
          onSaveClose={handleSaveClose}
          onSaveAssignments={handleSaveAssignments}
          assignmentsDirty={assignmentsDirty}
          onPrint={() => setPrintOpen(true)}
          onCancel={handleCancel}
          onArchive={handleArchive}
          onDelete={handleDelete}
          isSaving={saveMut.isPending}
          isSubmitting={submitMut.isPending || saveSubmitMut.isPending}
          isCancelling={cancelMut.isPending}
          isArchiving={archiveMut.isPending}
          isDeleting={deleteMut.isPending}
          isPrinting={false}
          myRowsComplete={myRowsComplete}
          myRowsCount={myRowsCount}
          requiresDisposition={mode.showWonLostBar}
        />

        <CustomerPickerModal
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={handleCustomerPick}
        />
        <PrintPreviewModal
          crId={crId}
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
        />
      </Container>
    </div>
  );
}

/**
 * Render the completion indicator badge for a tab.
 * - Returns null when there's nothing to show (no rows yet, or N/A tab).
 * - Returns a green check Badge when all responses are complete.
 * - Returns a "done/total" warning Badge with "N response(s) left" tooltip.
 *
 * Returns a React node that's plugged into ResponsiveTabs' per-tab `badge`
 * slot — same visual indicator as before, just packaged for the kit's
 * tab strip component instead of a bespoke <NavItem>.
 */
function renderCompletionBadge(completion) {
  if (!completion || completion.total === 0) return null;
  if (completion.remaining === 0) {
    return (
      <Badge color="success" pill title="All responses complete">
        <i className="ri-check-line" />
      </Badge>
    );
  }
  return (
    <Badge color="warning" pill title={`${completion.remaining} response(s) left`}>
      {completion.done}/{completion.total}
    </Badge>
  );
}

function emptyForm() {
  return {
    title: "",
    custName: "",
    custId: "",
    salesRepCode: "",
    salesOrderNum: null,
    quoteNumber: null,
    semiDevelopmental: false,
    developmental: false, // S6 — set by Set Defaults once the user picks Dev / Semi-Dev
    itarApply: null,
    statusRfq: false,
    statusQuoted: false,
    statusPoReceived: false,
    crCompletionDate: new Date(Date.now() + 7 * 24 * 3600_000)
      .toISOString()
      .slice(0, 10),
    stageSimilarToExistingPart: false,
    stageLeadTime: false,
    stageNewCosting: false,
    stageContractModification: false,
    stageLengthChange: false,
    stageQaTerms: false,
    stageInitialOpportunity: false,
    stageOther: false,
    descriptionOfPostQuoteChanges: "",
  };
}

// N9 — serialised form + grids, compared with the last-loaded baseline.
function snapshotOf(f, k, sa, e, m, q) {
  return JSON.stringify([f, k, sa, e, m, q]);
}

// N3 — true when a yyyy-MM-dd[...] date is today's local date.
function isToday(d) {
  if (!d) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return String(d).slice(0, 10) === today;
}

function patch(rows, i, p) {
  const next = rows.slice();
  next[i] = { ...next[i], ...p };
  return next;
}

function extractSswPart(keyRows) {
  const r = (keyRows || []).find((x) => /ssw part/i.test(x.questionText || ""));
  return r?.response || null;
}

function extractErr(e) {
  return (
    e?.response?.data?.message ||
    e?.response?.data ||
    e?.message ||
    "Operation failed."
  );
}

function computeStatusBanner(cr, mode, myEmpId, completionStats) {
  if (!cr) return null;
  const me = Number(myEmpId);
  if (cr.saved)
    return {
      color: "warning",
      text: "This CR is pending initial submission. No responses will be collected until it is submitted.",
    };
  if (cr.archived) return { color: "secondary", text: "This CR is archived." };
  if (cr.isReady) {
    if (Number(cr.createdAuthor) === me) {
      const isQuote = (cr.quoteNumber ?? 0) > 0;
      return {
        color: "success",
        text: isQuote
          ? "All responses are in. Final step: mark the quote Won or Lost in the bar above, then it will be approved and archived."
          : "All responses are in. Final step: click \"Approve & Archive (Final)\" at the bottom to finish this CR.",
      };
    }
    return {
      color: "success",
      text: `This CR has collected all required responses. Pending ${cr.createdAuthorName}'s final review.`,
    };
  }

  // --- In-progress: build a human breakdown of what's still outstanding. ---
  // We mirror the legacy two-stage cascade so users see exactly what gate they
  // are currently behind:
  //   • DeptsNotified=false → only the Key department is collecting responses
  //   • DeptsNotified=true  → all 5 grids (minus NA depts) are collecting
  const pendingParts = [];
  if (completionStats) {
    const keyPart = completionStats.key;
    if (!cr.deptsNotified) {
      // Pre-Stage 2: only Key dept matters; other depts shouldn't be touched yet.
      if (keyPart && keyPart.remaining > 0) {
        pendingParts.push(`Key Details (${keyPart.remaining} left)`);
      }
    } else {
      // Post-Stage 2: full grid set is live.
      const map = [
        ["Key Details", completionStats.key],
        ["Sales", completionStats.sales],
        cr.engineeringNA ? null : ["Engineering", completionStats.eng],
        cr.materialsNA ? null : ["Materials", completionStats.mat],
        cr.qualityNA ? null : ["Quality", completionStats.qa],
      ].filter(Boolean);
      for (const [label, stat] of map) {
        if (stat && stat.remaining > 0) {
          pendingParts.push(`${label} (${stat.remaining} left)`);
        }
      }
    }
  }

  const youOwe = mode.amResponsible;

  if (pendingParts.length > 0) {
    const lead = !cr.deptsNotified
      ? "Awaiting Key Department before other departments are notified — pending:"
      : "Awaiting responses from:";
    return {
      color: youOwe ? "info" : "info",
      text: `${lead} ${pendingParts.join(" • ")}${youOwe ? " — one or more of these are assigned to YOU." : ""}`,
    };
  }

  if (youOwe)
    return {
      color: "info",
      text: "This CR is currently awaiting one or more responses from you.",
    };
  if (Number(cr.createdAuthor) === me)
    return {
      color: "info",
      text: "This CR is currently collecting responses from everyone. You may change the designated employee for any pending response.",
    };
  return null;
}
